import { Avatar, Box, Icon, Spinner, Table, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import usePagination from 'modules/core/hooks/usePagination'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'
import { getFullDisplayName } from 'modules/core/lib/userDisplayName'
import users from 'modules/core/store/collections/users'

import type Employee from 'modules/employees/store/models/Employee'
import { useSearchEmployees } from 'modules/payroll_bundle/hooks/useSearchEmployees'

import TableOptions from './TableOptions'

interface Props {
  employees: Employee[]
  loading: boolean
}

const EMPLOYEES_PER_PAGE = 50

const EmployeesTable = ({ employees, loading }: Props) => {
  const { navigation } = useNavigation()

  const {
    paginatedItems: paginatedEmployees,
    loadMore,
    hasMore,
    page,
  } = usePagination({
    batchSize: EMPLOYEES_PER_PAGE,
    items: employees.sort((a, b) => {
      const nameA =
        (() => {
          const u = users.get(a.attributes.access_id)
          return u
            ? getFullDisplayName({
                firstName: u.attributes.first_name,
                lastName: u.attributes.last_name,
                canSharePreferredName: a.policy.can('sharePreferredName'),
                preferredName: a.attributes.preferred_name,
              })
            : ''
        })() ?? ''
      const nameB =
        (() => {
          const u = users.get(b.attributes.access_id)
          return u
            ? getFullDisplayName({
                firstName: u.attributes.first_name,
                lastName: u.attributes.last_name,
                canSharePreferredName: b.policy.can('sharePreferredName'),
                preferredName: b.attributes.preferred_name,
              })
            : ''
        })() ?? ''
      return nameA.localeCompare(nameB)
    }),
  })
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()
  const filteredEmployees = useSearchEmployees(debouncedSearch, paginatedEmployees).get()
  const isEmpty = filteredEmployees.length === 0

  return (
    <Table options={() => <TableOptions search={search} onSearch={onSearch} disabled={loading} />}>
      <Table.BodyWithInfiniteScroll page={page} loadMore={loadMore} hasMore={hasMore} colSpan={1}>
        {loading && (
          <Table.Row>
            <Table.Cell>
              <Box alignItems="center">
                <Spinner />
              </Box>
            </Table.Cell>
          </Table.Row>
        )}

        {isEmpty && !loading && (
          <Table.Blank
            colSpan={1}
            text={i18n.t(
              'app_store_v2.apps.payroll_bundle.settings.payroll_policy.employees.empty_label'
            )}
          />
        )}

        {!isEmpty &&
          !loading &&
          filteredEmployees.map((employee) => (
            <Table.Row.Router
              key={employee.id}
              to={navigation.employees.id.fullPath({ id: employee.id })}
            >
              <Table.Cell>
                <Box flexDirection="row" gap="s12" alignItems="center">
                  <Avatar
                    size="s40"
                    url={buildUrl(users.get(employee.attributes.access_id)?.avatar)}
                    label={
                      (() => {
                        const u = users.get(employee.attributes.access_id)
                        return u
                          ? getFullDisplayName({
                              firstName: u.attributes.first_name,
                              lastName: u.attributes.last_name,
                              canSharePreferredName: employee.policy.can('sharePreferredName'),
                              preferredName: employee.attributes.preferred_name,
                            })
                          : ''
                      })() ?? ''
                    }
                  />
                  <Text.Normal color="grey1000">
                    {(() => {
                      const u = users.get(employee.attributes.access_id)
                      return u
                        ? getFullDisplayName({
                            firstName: u.attributes.first_name,
                            lastName: u.attributes.last_name,
                            canSharePreferredName: employee.policy.can('sharePreferredName'),
                            preferredName: employee.attributes.preferred_name,
                          })
                        : ''
                    })()}
                  </Text.Normal>
                </Box>
              </Table.Cell>
              <Table.Cell colSpan={1}>
                <Box flexDirection="row" justifyContent="flexEnd">
                  <Icon.Medium icon="arrow-right" />
                </Box>
              </Table.Cell>
            </Table.Row.Router>
          ))}
      </Table.BodyWithInfiniteScroll>
    </Table>
  )
}

export default observer(EmployeesTable)
